// Copyright (C) Okahu Inc 2023-2024. All rights reserved.

// centralized service to manage the interval and API calls
import { getTenantTimeRange } from '@/utils/getTenantTimeRange';
import { MeasuresData } from 'types/measures-api-data';

import {
  getPresetLabelToNameMap,
  getPresetRange,
} from '@/components/display-page/prompts/date-range-picker/calendar-helper/presets';

import { request } from './request';

const INTERVAL_DURATION = 60 * 60 * 1000;
const STORAGE_KEY = 'measuresList';
const TIMESTAMP_KEY = 'measuresLastFetch';

class MeasuresManager {
  private intervalId: number | null = null;
  private isInitialized: boolean = false;
  private lastTimeRange: string | null = null;
  private appNames: string[] = [];
  private presets: any[] = [];

  public getLastTimeRange(): string | null {
    return this.lastTimeRange;
  }
  // Initizalize on app mount
  async initialize(appNames: string[], presets: any[]) {
    // Fetch immediately on first load
    if (this.isInitialized || appNames.length === 0) return;
    this.isInitialized = true;
    this.appNames = appNames;
    this.presets = presets;

    // Only fetch if data is stale or doesn't exist
    if (this.needsRefresh()) {
      await this.fetchAndStore(appNames, presets, true); // true = show loading
    }

    // Update tracked time range
    this.lastTimeRange = getTenantTimeRange();

    // Set up interval if not already running.
    if (!this.intervalId) {
      this.intervalId = window.setInterval(async () => {
        await this.fetchAndStore(this.appNames, this.presets, true); // true = show loading
      }, INTERVAL_DURATION);
    }
    // Listen for time range changes
    this.setupTimeRangeListener();
  }

  // Re-fetch measures whenever tenant's default time range changes
  private setupTimeRangeListener() {
    const handleTimeRangeChange = async () => {
      this.lastTimeRange = getTenantTimeRange();
      await this.fetchAndStore(this.appNames, this.presets, true);
    };

    window.removeEventListener('tenantTimeRangeChanged', handleTimeRangeChange);
    window.addEventListener('tenantTimeRangeChanged', handleTimeRangeChange);
  }

  // Fetch API and update localStorage
  private async fetchAndStore(
    appNames: string[],
    presets: any[],
    showLoading: boolean = false
  ) {
    if (appNames.length === 0) return;

    // Notify start of loading
    if (showLoading) {
      window.dispatchEvent(
        new CustomEvent('measuresLoading', { detail: true })
      );
    }

    // Get time range from tenant settings
    const timeRangeLabel = getTenantTimeRange();
    const presetMap = getPresetLabelToNameMap(presets);
    const presetName = timeRangeLabel ? presetMap[timeRangeLabel] : 'last7';
    const { from, to } = getPresetRange(presetName || 'last7', presets);

    const queryParams = {
      start_time: from?.toISOString() || new Date().toISOString(),
      end_time: to?.toISOString() || new Date().toISOString(),
    };

    localStorage.setItem('measuresEndTime', queryParams.end_time);

    try {
      const response: any = await request.post(
        { endpoint: 'insights/measures/query', queryParams },
        { app_names: appNames }
      );

      const measures: MeasuresData = response[0]?.measures || {};

      // Update localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(measures));
      localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());

      // Trigger storage event for other components
      window.dispatchEvent(new Event('measuresUpdated'));
    } catch (error) {
      console.error('Failed to fetch measures:', error);
    } finally {
      // Notify end of loading
      if (showLoading) {
        window.dispatchEvent(
          new CustomEvent('measuresLoading', { detail: false })
        );
      }
    }
  }

  // Check if data needs refresh (60min passed or tenant time range changed)
  needsRefresh(): boolean {
    const currentTimeRange = getTenantTimeRange();

    // Check if time range changed
    if (currentTimeRange !== this.lastTimeRange) {
      return true;
    }

    const lastFetch = localStorage.getItem(TIMESTAMP_KEY);
    if (!lastFetch) return true;

    const timeSinceLastFetch = Date.now() - parseInt(lastFetch);
    return timeSinceLastFetch >= INTERVAL_DURATION;
  }

  // Manual refresh (for home page mount)
  async refresh(appNames: string[], presets: any[]) {
    this.lastTimeRange = getTenantTimeRange();
    await this.fetchAndStore(appNames, presets, true);
  }

  // Reset initialization state (useful for logout)
  reset() {
    this.isInitialized = false;
    this.lastTimeRange = null;
    this.destroy();
  }

  // Cleanup
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

export const measuresManager = new MeasuresManager();
